import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BRANDING_FONTS, DEFAULT_BRANDING } from '@grc/types';
import type {
  BrandingConfig,
  BrandingFont,
  HeadingStyle,
  OrgBranding,
  UpdateOrgBrandingInput,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  useToast,
} from '../../components/ui.js';

const MAX_LOGO_BYTES = 50 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg'];

type HeadingKey = 'h1' | 'h2' | 'h3';
const HEADING_LABELS: Record<HeadingKey, string> = {
  h1: 'Heading 1 (document title)',
  h2: 'Heading 2 (sections)',
  h3: 'Heading 3 (sub-sections)',
};

function fontCss(key: BrandingFont): string {
  return BRANDING_FONTS.find((f) => f.key === key)?.css ?? BRANDING_FONTS[0].css;
}

function FontSelect({
  id,
  value,
  onChange,
  ...rest
}: {
  id: string;
  value: BrandingFont;
  onChange: (font: BrandingFont) => void;
  'aria-describedby'?: string;
}) {
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value as BrandingFont)} {...rest}>
      {BRANDING_FONTS.map((f) => (
        <option key={f.key} value={f.key}>
          {f.label}
        </option>
      ))}
    </Select>
  );
}

export function BrandingPage() {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['branding', org.orgId],
    queryFn: () => apiFetch<OrgBranding>(`/orgs/${org.orgId}/branding`),
    enabled: isAdmin,
  });

  const [config, setConfig] = useState<BrandingConfig>(DEFAULT_BRANDING);
  // Dirty guard: background refetches (e.g. after a logo upload/remove
  // invalidates ['branding']) must not clobber unsaved config edits, so
  // hydrate only while pristine.
  const configDirty = useRef(false);
  useEffect(() => {
    if (data && !configDirty.current) setConfig(data.config);
  }, [data]);

  // Logo preview: the endpoint returns binary, so fetch as a blob with
  // credentials and show it via an object URL (revoked on change/unmount).
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!data?.hasLogo) {
      setLogoSrc(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetch(apiUrl(`/orgs/${org.orgId}/branding/logo`), { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        // Re-check after the await: if this effect was cleaned up while the
        // body was still downloading, abandon before creating an object URL —
        // otherwise it would never be revoked and a stale logo could replace
        // a newer preview.
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoSrc(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [org.orgId, data?.hasLogo, data?.updatedAt]);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Could not save branding', 'error');
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['branding', org.orgId] });

  const save = useMutation({
    mutationFn: (body: UpdateOrgBrandingInput) =>
      apiFetch<OrgBranding>(`/orgs/${org.orgId}/branding`, { method: 'PUT', body }),
    onSuccess: (saved) => {
      configDirty.current = false;
      setConfig(saved.config);
      invalidate();
      toast('Branding saved.', 'success');
    },
    onError,
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<OrgBranding>(`/orgs/${org.orgId}/branding/logo`, { method: 'PUT', formData });
    },
    onSuccess: () => {
      setLogoFile(null);
      if (fileInput.current) fileInput.current.value = '';
      invalidate();
      toast('Logo uploaded.', 'success');
    },
    onError,
  });

  const removeLogo = useMutation({
    mutationFn: () => apiFetch<void>(`/orgs/${org.orgId}/branding/logo`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast('Logo removed.', 'success');
    },
    onError,
  });

  if (!isAdmin) {
    return (
      <EmptyState
        title="Admins only"
        message="Export branding can only be managed by organisation admins."
      />
    );
  }
  if (isError) {
    return (
      <div>
        <PageHeader title="Branding" />
        <EmptyState
          title="Could not load branding"
          message="Something went wrong loading export branding — please try again shortly."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }
  if (isLoading || !data) return <LoadingPane label="Loading branding" />;

  const selectLogo = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      return;
    }
    if (!LOGO_TYPES.includes(file.type)) {
      toast('Logo must be a PNG or JPEG image.', 'error');
      if (fileInput.current) fileInput.current.value = '';
      setLogoFile(null);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast('Logo must be 50 MB or smaller.', 'error');
      if (fileInput.current) fileInput.current.value = '';
      setLogoFile(null);
      return;
    }
    setLogoFile(file);
  };

  // All user edits go through these helpers so the dirty flag is set on the
  // first edit and query hydration stops overwriting in-progress changes.
  const updateConfig = (patch: Partial<BrandingConfig>) => {
    configDirty.current = true;
    setConfig((c) => ({ ...c, ...patch }));
  };

  const setHeading = (key: HeadingKey, patch: Partial<HeadingStyle>) => {
    configDirty.current = true;
    setConfig((c) => ({
      ...c,
      headings: { ...c.headings, [key]: { ...c.headings[key], ...patch } },
    }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(config);
  };

  const previewHeading = (key: HeadingKey) => {
    const style = config.headings[key];
    return {
      fontFamily: fontCss(style.fontFamily),
      fontSize: `${Number.isFinite(style.sizePt) ? style.sizePt : 12}pt`,
      color: style.color,
      margin: '0 0 8px',
    };
  };

  return (
    <div>
      <PageHeader
        title="Branding"
        subtitle="Header, footer, logo and typography applied to exported documents (PDF, DOCX and HTML)"
      />

      <form onSubmit={submit}>
        <Card title="Page header & footer">
          <Field label="Header text" hint="Shown at the top of every exported document">
            {(props) => (
              <Input
                {...props}
                maxLength={300}
                value={config.headerText}
                onChange={(e) => updateConfig({ headerText: e.target.value })}
              />
            )}
          </Field>
          <Field label="Footer text" hint="Shown at the end of every exported document">
            {(props) => (
              <Input
                {...props}
                maxLength={300}
                value={config.footerText}
                onChange={(e) => updateConfig({ footerText: e.target.value })}
              />
            )}
          </Field>
        </Card>

        <Card title="Logo">
          {data.hasLogo && logoSrc && (
            <div style={{ marginBottom: 12 }}>
              <img
                src={logoSrc}
                alt="Current organisation logo"
                style={{
                  maxHeight: 80,
                  maxWidth: '100%',
                  border: '1px solid var(--color-border)',
                  borderRadius: 4,
                  padding: 4,
                  background: '#fff',
                }}
              />
            </div>
          )}
          {!data.hasLogo && (
            <p style={{ color: 'var(--color-text-muted)' }}>No logo uploaded yet.</p>
          )}
          <Field label="Logo file" hint="PNG or JPEG, up to 50 MB">
            {(props) => (
              <input
                {...props}
                className="input"
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => selectLogo(e.target.files?.[0] ?? null)}
              />
            )}
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              disabled={!logoFile}
              loading={uploadLogo.isPending}
              onClick={() => logoFile && uploadLogo.mutate(logoFile)}
            >
              Upload logo
            </Button>
            {data.hasLogo && (
              <Button
                variant="danger"
                loading={removeLogo.isPending}
                onClick={() => removeLogo.mutate()}
              >
                Remove logo
              </Button>
            )}
          </div>
        </Card>

        <Card title="Typography">
          <Field label="Body font" hint="Used for paragraph text in every export format">
            {(props) => (
              <FontSelect
                {...props}
                value={config.fontFamily}
                onChange={(font) => updateConfig({ fontFamily: font })}
              />
            )}
          </Field>
          {(Object.keys(HEADING_LABELS) as HeadingKey[]).map((key) => {
            const style = config.headings[key];
            return (
              <div key={key} style={{ marginTop: 12 }}>
                <h4 style={{ margin: '0 0 8px' }}>{HEADING_LABELS[key]}</h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: 12,
                  }}
                >
                  <Field label={`${key.toUpperCase()} size (pt)`}>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        required
                        min={8}
                        max={72}
                        step={1}
                        value={Number.isFinite(style.sizePt) ? style.sizePt : ''}
                        onChange={(e) => setHeading(key, { sizePt: e.target.valueAsNumber })}
                      />
                    )}
                  </Field>
                  <Field label={`${key.toUpperCase()} font`}>
                    {(props) => (
                      <FontSelect
                        {...props}
                        value={style.fontFamily}
                        onChange={(font) => setHeading(key, { fontFamily: font })}
                      />
                    )}
                  </Field>
                  <Field label={`${key.toUpperCase()} colour`}>
                    {(props) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          {...props}
                          type="color"
                          value={style.color}
                          onChange={(e) => setHeading(key, { color: e.target.value })}
                          style={{
                            // Render as a solid swatch block rather than the
                            // browser's thin default strip; the background
                            // fills the whole control with the chosen colour.
                            width: 48,
                            height: 32,
                            padding: 0,
                            border: '1px solid var(--color-border)',
                            borderRadius: 6,
                            background: style.color,
                            cursor: 'pointer',
                          }}
                        />
                        <code aria-hidden="true" style={{ fontSize: '0.85em' }}>
                          {style.color}
                        </code>
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            );
          })}
        </Card>

        <Card title="Preview">
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
            An approximation of how exported documents will look with the settings above.
          </p>
          <div
            aria-label="Export preview"
            style={{
              background: '#fff',
              color: '#1a202c',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              padding: 24,
              fontFamily: fontCss(config.fontFamily),
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                borderBottom: '1px solid #cbd5e0',
                paddingBottom: 8,
                marginBottom: 16,
                fontSize: '9pt',
                color: '#4a5568',
              }}
            >
              <span>{config.headerText || ' '}</span>
              {logoSrc && (
                <img src={logoSrc} alt="" style={{ maxHeight: 32, maxWidth: 160 }} />
              )}
            </div>
            <h1 style={previewHeading('h1')}>Information Security Assessment</h1>
            <h2 style={previewHeading('h2')}>1. Governance</h2>
            <h3 style={previewHeading('h3')}>1.1 Policies and responsibilities</h3>
            <p style={{ margin: 0, fontSize: '11pt', lineHeight: 1.6 }}>
              The organisation maintains an information security policy which is reviewed
              annually and approved by senior management. Responsibilities are assigned to
              named owners and communicated to all staff.
            </p>
            <div
              style={{
                borderTop: '1px solid #cbd5e0',
                paddingTop: 8,
                marginTop: 16,
                fontSize: '9pt',
                color: '#4a5568',
              }}
            >
              {config.footerText || ' '}
            </div>
          </div>
        </Card>

        <Button type="submit" loading={save.isPending}>
          Save branding
        </Button>
      </form>
    </div>
  );
}
